import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { featureAccess } from "../permissions/access";
import { useUserRole } from "../utils/hooks/useUserRole";
import { getBranch, getBranchReadiness } from "../services/branch";
import { BranchReadiness, BranchResponse } from "../types/branch.type";
import BranchInfoForm from "../components/Branch/BranchInfoForm";
import BranchInfoView from "../components/Branch/BranchInfoView";
import ReadinessChecklist from "../components/Branch/ReadinessChecklist";
import { BRANCH_LIST_PATH, BRANCH_MENU_TITLE } from "../components/Branch/constants";

export default function BranchDetailPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const role = useUserRole();
  const canEditBranch = featureAccess.branchManagementWrite(role);

  const [branch, setBranch] = useState<BranchResponse | null>(null);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [readiness, setReadiness] = useState<BranchReadiness | null>(null);
  const [readinessFailed, setReadinessFailed] = useState<boolean>(false);

  // A failed load has already been reported by the interceptor; the page only has to say
  // there is nothing to show instead of spinning.
  const fetchBranch = useCallback(async () => {
    if (!uuid) return;
    try {
      const {
        data: { data },
      } = await getBranch(uuid);
      setBranch(data);
      setNotFound(!data);
    } catch {
      setNotFound(true);
    }
  }, [uuid]);

  const fetchReadiness = useCallback(async () => {
    if (!uuid) return;
    try {
      const {
        data: { data },
      } = await getBranchReadiness(uuid);
      setReadiness(data);
      setReadinessFailed(!data);
    } catch {
      setReadinessFailed(true);
    }
  }, [uuid]);

  useEffect(() => {
    fetchBranch();
  }, [fetchBranch]);

  useEffect(() => {
    fetchReadiness();
  }, [fetchReadiness]);

  const breadcrumb = (
    <Stack direction="row" alignItems="center" gap={1} mb={2}>
      <Link to={BRANCH_LIST_PATH} style={{ textDecoration: "none", color: "inherit" }}>
        <Typography variant="body1" fontWeight={500}>
          {BRANCH_MENU_TITLE}
        </Typography>
      </Link>
      <Typography variant="body1">/</Typography>
      <Typography variant="body1" color="primary">
        {branch?.branch_code ?? "…"}
      </Typography>
    </Stack>
  );

  if (!branch) {
    return (
      <Box sx={{ padding: "8px 0" }}>
        {breadcrumb}
        {notFound ? (
          <Typography color="text.secondary">ไม่พบสาขานี้</Typography>
        ) : (
          <Stack alignItems="center" py={8}>
            <CircularProgress />
          </Stack>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ padding: "8px 0" }}>
      {breadcrumb}

      <Typography variant="h6" fontWeight={700} mb={2}>
        {branch.branch_code} {branch.branch_name}
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={2}>
          ข้อมูลสาขา
        </Typography>
        {canEditBranch ? (
          <BranchInfoForm branch={branch} onSaved={fetchBranch} />
        ) : (
          <BranchInfoView branch={branch} />
        )}
      </Paper>

      <Paper>
        <Typography variant="subtitle1" fontWeight={700} mb={2}>
          เช็คลิสต์ความพร้อม
        </Typography>
        <ReadinessChecklist
          readiness={readiness}
          loadFailed={readinessFailed}
          canOpenRoutes={canEditBranch}
          onReadinessChanged={fetchReadiness}
        />
      </Paper>
    </Box>
  );
}
